import React, { useState, KeyboardEvent } from "react";
import {
  Box,
  Button,
  InputBase,
  Divider,
  CircularProgress,
} from "@mui/material";
import { formatVideoTimestamp } from "@/utils/videoUtils";

interface PublicCommentInputProps {
  currentVideoTime: number;
  onSubmit: (comment: string, timestamp: number, authorName: string) => Promise<void>;
  isSubmitting: boolean;
  isImageMode?: boolean;
}

const PublicCommentInput: React.FC<PublicCommentInputProps> = ({
  currentVideoTime,
  onSubmit,
  isSubmitting,
  isImageMode = false,
}) => {
  const [authorName, setAuthorName] = useState("");
  const [comment, setComment] = useState("");

  const handleSubmit = async () => {
    if (!comment.trim() || !authorName.trim()) return;

    try {
      await onSubmit(comment.trim(), currentVideoTime, authorName.trim());
      setComment("");
      // Keep the author name for convenience if they want to add more comments
    } catch (error) {
      console.error("Error submitting comment:", error);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && comment.trim() && authorName.trim()) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSubmit = comment.trim() && authorName.trim() && !isSubmitting;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #e0e0e0",
        backgroundColor: "#fff",
        px: 2,
        py: 1.5,
      }}
    >
      {/* Author Name Input */}
      <InputBase
        placeholder="Your name"
        value={authorName}
        onChange={(e) => setAuthorName(e.target.value)}
        disabled={isSubmitting}
        sx={{
          flex: "0 0 auto",
          minWidth: 100,
          maxWidth: 120,
          fontSize: "0.875rem",
          color: "#333",
          "& input::placeholder": {
            color: "#999",
            opacity: 1,
          },
        }}
      />

      {/* Vertical Divider */}
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          mx: 1.5,
          height: 24,
          alignSelf: "center",
          borderColor: "#e0e0e0",
        }}
      />

      {/* Comment Input */}
      <InputBase
        placeholder="Add a comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isSubmitting}
        multiline
        maxRows={3}
        sx={{
          flex: 1,
          fontSize: "0.875rem",
          color: "#333",
          "& input::placeholder, & textarea::placeholder": {
            color: "#999",
            opacity: 1,
          },
        }}
      />

      {/* Submit Button */}
      <Button
        onClick={handleSubmit}
        disabled={!canSubmit}
        variant="contained"
        size="small"
        sx={{
          ml: 1,
          textTransform: "none",
          fontSize: "0.8rem",
          whiteSpace: "nowrap",
          backgroundColor: canSubmit ? "#6633d1" : "#ccc",
          "&:hover": {
            backgroundColor: canSubmit ? "#5228a8" : "#ccc",
          },
          "&.Mui-disabled": {
            backgroundColor: "#e0e0e0",
            color: "#999",
          },
        }}
      >
        {isSubmitting ? (
          <CircularProgress size={16} color="inherit" />
        ) : isImageMode ? (
          "Add Comment"
        ) : (
          `Comment at ${formatVideoTimestamp(currentVideoTime)}`
        )}
      </Button>
    </Box>
  );
};

export default PublicCommentInput;
